import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 30, suffix: '+', label: 'Años en el Mercado', icon: 'ri-award-line' },
  { value: 50, suffix: '+', label: 'Productos en Catálogo', icon: 'ri-flask-line' },
  { value: 200, suffix: '+', label: 'Clientes Activos', icon: 'ri-group-line' },
  { value: 30, suffix: '+', label: 'Importación Nacional', icon: 'ri-earth-line' },
];

function useCountUp(target: number, active: boolean, duration = 2000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);

  return count;
}

function StatCard({ stat, active, delay }: { stat: typeof stats[0]; active: boolean; delay: number }) {
  const count = useCountUp(stat.value, active);

  return (
    <div
      className="flex flex-col items-center p-8 text-center"
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/10 mb-4">
        <i className={`${stat.icon} text-white text-2xl`}></i>
      </div>
      <div className="font-heading font-700 text-5xl md:text-6xl text-white mb-2">
        {count}<span className="text-accent-400">{stat.suffix}</span>
      </div>
      <div className="text-primary-200 text-sm font-500 tracking-wide">{stat.label}</div>
    </div>
  );
}

export default function Stats() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-16 md:py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20large%20industrial%20petrochemical%20plant%20complex%20at%20dusk%2C%20wide%20panoramic%20shot%2C%20atmospheric%20industrial%20landscape%2C%20warm%20amber%20and%20dark%20blue%20tones%2C%20oil%20refinery%20infrastructure%2C%20dramatic%20sky%2C%20ultra-wide%20cinematic%20industrial%20photography&width=1800&height=600&seq=stats-bg-industrial-01&orientation=landscape"
          alt="Industria química"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-primary-700/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 via-primary-700/70 to-accent-800/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className={`text-center mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="font-heading font-700 text-3xl md:text-4xl text-white">
            Poly Alphas Chemicals C.A. en Números
          </h2>
        </div>
        <div className={`grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/20 divide-y lg:divide-y-0 transition-all duration-700 delay-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          {stats.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} active={visible} delay={i * 0.15} />
          ))}
        </div>
      </div>
    </section>
  );
}